import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import libraryService from '../services/libraryService';
import deleteService from '../services/deleteService';
import { usePatientFilters } from '../hooks/usePatientFilters';
import { useStudyFilters } from '../hooks/useStudyFilters';
import InfoModal from '../components/InfoModal';
import ShareModal from '../components/ShareModal';
import DeleteModal from '../components/DeleteModal';
import Icon from '@mdi/react';
import { makeStyles } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Popover,
  Snackbar
} from '@material-ui/core';

import { Alert } from '@material-ui/lab';

import { 
  mdiCloudUploadOutline,
  mdiLogout,
  mdiMagnify,
  mdiClose,
  mdiChevronUp,
  mdiChevronDown,
  mdiInformationOutline,
  mdiAccountPlusOutline,
  mdiMenuDown,
  mdiMenuUp,
  mdiCalendar,
  mdiTrashCanOutline,
} from '@mdi/js';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: theme.palette.background.default,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 3),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  content: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  pane: {
    flex: 1,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  paneTitle: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontWeight: 600,
  },
  scrollableList: {
    flex: 1,
    overflow: 'auto',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    paddingTop: 0,    
  },
  cardContainer: {
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: `2px solid transparent`,
    '&:hover': {
      boxShadow: theme.shadows[4],
      borderColor: theme.palette.action.hover,
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedCard: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      boxShadow: 'none',
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
  },
  studyCard: {
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: `2px solid transparent`,
    '&:hover': {
      boxShadow: theme.shadows[4],
      borderColor: theme.palette.action.hover,
      backgroundColor: theme.palette.action.hover,
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  patientName: {
    fontWeight: 600,
    fontSize: '1rem',
  },
  studyDescription: {
    fontWeight: 600,
    fontSize: '1rem',
  },
  phiInfo: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.5),
  },
  studyInfo: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.5),
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(4),
  },
  emptyStateText: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  searchContainer: {
    padding: theme.spacing(2, 2, 0),
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
    },
  },
  sortFilterContainer: {
    padding: theme.spacing(1.5, 2, 0),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  sortLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  filterRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  filterMiddle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  filterLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
  filterInput: {
    width: 95,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
    },
    '& input': {
      padding: theme.spacing(1, 1),
      fontSize: '0.875rem',
    },
    '& .MuiSelect-select': {
      padding: theme.spacing(1, 1),
      fontSize: '0.875rem',
    },
  },
  yearSelect: {
    width: 95,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
    },
    '& .MuiSelect-select': {
      padding: theme.spacing(1, 1),
      fontSize: '0.875rem',
    },
  },
  yearSelectPlaceholder: {
    color: '#999',
    fontWeight: 500,
  },
  yearSelectMenu: {
    maxHeight: 300,
  },
  filterDatePicker: {
    width: 115,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      paddingRight: 0,
      cursor: 'pointer',
    },
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(1, 1),
      paddingRight: 0,
      fontSize: '0.875rem',
      cursor: 'pointer',
    },
    '& .MuiInputBase-root': {
      fontSize: '0.875rem',
    },
    '& .MuiIconButton-root': {
      pointerEvents: 'none',
      padding: 0,
      marginRight: '10px',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  filterToText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  filterButton: {
    fontSize: '0.875rem',
    padding: theme.spacing(0.5, 1.5),
    minWidth: 'auto',
    textTransform: 'none',
  },
  sortLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
  patientSortSelect: {
    width: 130,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
    },
    '& .MuiSelect-select': {
      padding: theme.spacing(1, 1),
      fontSize: '0.875rem',
    },
  },
  studySortSelect: {
    width: 120,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
    },
    '& .MuiSelect-select': {
      padding: theme.spacing(1, 1),
      fontSize: '0.875rem',
    },
  },
  sortButton: {
    fontSize: '0.875rem',
    padding: theme.spacing(0.5, 1.5),
    minWidth: 'auto',
    textTransform: 'none',
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    '&.active': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  sortIcon: {
    marginLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  modalityButton: {
    fontSize: '0.875rem',
    fontWeight: 400,
    padding: theme.spacing(0.5, 1.5),
    width: 80,
    textTransform: 'none',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
    borderColor: theme.palette.divider,
    '& .MuiButton-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
    },
    '& .MuiButton-endIcon': {
      marginLeft: theme.spacing(0.5),
    },
    '&:hover': {
      backgroundColor: theme.palette.background.default,
      borderColor: theme.palette.text.primary,
    },
    '&:focus': {
      backgroundColor: theme.palette.background.default,
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
    '&.Mui-focusVisible': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
    '&.open': {
      backgroundColor: theme.palette.background.default,
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
  },
  modalityPopover: {
    '& .MuiPopover-paper': {
      padding: theme.spacing(2),
      marginTop: theme.spacing(0.1),
      width: 135,
    },
  },
  modalityCheckbox: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
    },
  },
  modalityActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  selectionHeader: {
    padding: theme.spacing(1.5, 2),
    // display: 'flex',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  selectionControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(0.5, 1.2),
    borderRadius: theme.spacing(0.5),
  },
  selectionActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  selectionCount: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    minWidth: 130,
  },
  cardCheckboxContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(-1),
  },
  cardContent: {
    flex: 1,
    minWidth: 0, // Allow text truncation
  },
}));

// Helper function to generate year menu items
const generateYearMenuItems = () => {
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let year = 1900; year <= currentYear; year++) {
    years.push(
      <MenuItem key={year} value={year.toString()}>
        {year}
      </MenuItem>
    );
  }
  return years;
};

const Library = () => {
  const classes = useStyles();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Search states
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [studySearchQuery, setStudySearchQuery] = useState('');
  
  // Use custom hooks for filtering and sorting
  const patientFiltersHook = usePatientFilters(patients, patientSearchQuery);
  const studyFiltersHook = useStudyFilters(selectedPatient, studySearchQuery);
  
  // Info modal states
  const [patientInfoModal, setPatientInfoModal] = useState({
    open: false,
    patient: null,
  });
  const [studyInfoModal, setStudyInfoModal] = useState({
    open: false,
    study: null,
  });
  
  // Share modal states
  const [shareModal, setShareModal] = useState({
    open: false,
    type: null, // 'patient' or 'study'
    item: null,
  });

  // Delete modal states
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    type: null, // 'patients' or 'studies'
  });

  // Selection state - studies are source of truth
  const [selectedStudyIds, setSelectedStudyIds] = useState(new Set());

  // Track which panel controls selection ('patients', 'studies', or null)
  const [activeSelectionPanel, setActiveSelectionPanel] = useState(null);

  // Load patients on mount
  useEffect(() => {
    loadPatients();
  }, []);

  // Restore selected patient from navigation state
  useEffect(() => {
    if (location.state?.patientId && patients.length > 0) {
      const patient = patients.find(p => p.id === location.state.patientId);
      if (patient) {
        setSelectedPatient(patient);
      }
    }
  }, [location.state, patients]);

  // Clear study search when selected patient changes
  useEffect(() => {
    setStudySearchQuery('');
  }, [selectedPatient]);

  /**
   * Load all accessible patients from the library service
   * Updates patients state and handles loading state
   */
  const loadPatients = async () => {
    setIsLoading(true);
    try {
      const response = await libraryService.listAccessiblePatients();
      if (response.success) {
        setPatients(response.data);
      } else {
        // Show error message to user
        console.error('Failed to load studies:', response.error);
        // TODO: Add toast/snackbar notification for error
      }
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle user logout and redirect to login page
   */
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handlePatientClick = (patient) => {
    // If any selection exists, clear it first
    if (hasSelection()) {
      setSelectedStudyIds(new Set());
      setActiveSelectionPanel(null);
    }
    
    // Then perform normal behavior
    setSelectedPatient(patient);
  };

  const handlePatientInfo = (e, patient) => {
    e.stopPropagation(); // Prevent card selection
    setPatientInfoModal({ open: true, patient });
  };

  /**
   * Open study in viewer in a new browser tab
   * @param {Object} study - Study object with id
   */
  const handleStudyClick = (study) => {
    // If any selection exists, clear it first
    if (hasSelection()) {
      setSelectedStudyIds(new Set());
      setActiveSelectionPanel(null);
    }
    
    // Then perform normal behavior - open viewer in a new tab
    const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
    const viewerUrl = `${base}/#/viewer/${study.id}`;
    window.open(viewerUrl, '_blank');
  };

  const handleStudyInfo = (e, study) => {
    e.stopPropagation(); // Prevent card click
    setStudyInfoModal({ open: true, study });
  };

  /**
   * Handle Share button click in Patients panel
   */
  const handlePatientsShareClick = () => {
    setShareModal({ open: true, type: 'patient', item: null });
  };

  /**
   * Handle Share button click in Studies panel
   */
  const handleStudiesShareClick = () => {
    setShareModal({ open: true, type: 'study', item: null });
  };

  /**
   * Handle Delete button click in Patients panel
   */
  const handlePatientsDeleteClick = () => {
    setDeleteModal({ open: true, type: 'patients' });
  };

  /**
   * Handle Delete button click in Studies panel
   */
  const handleStudiesDeleteClick = () => {
    setDeleteModal({ open: true, type: 'studies' });
  };

  /**
   * Handle Delete confirmation
   */
  const handleDeleteConfirm = async () => {
    const payload = getSelectionPayload();
    const studyIds = payload.studies.map(study => study.id);
    
    setIsDeleting(true);
    
    try {
      const result = await deleteService.batchDeleteStudies(studyIds, 'User requested deletion');
      
      if (result.success) {
        console.log('Delete successful:', result.data);
        
        // Refresh library data to remove deleted studies from UI
        await loadPatients();

        // Clear selected patient to force studies panel to refresh
        setSelectedPatient(null);
        
        const deletedCount = result.data.deleted_ids?.length || 0;
        const failedCount = result.data.failed?.length || 0;
        
        // Show appropriate message based on results
        if (deletedCount > 0 && failedCount > 0) {
          // Partial success
          setSnackbar({
            open: true,
            message: `Deleted ${deletedCount} ${deletedCount === 1 ? 'study' : 'studies'}. ${failedCount} ${failedCount === 1 ? 'study' : 'studies'} could not be deleted (insufficient permissions).`,
            severity: 'warning',
          });
        } else if (deletedCount > 0) {
          // Full success
          setSnackbar({
            open: true,
            message: `Successfully deleted ${deletedCount} ${deletedCount === 1 ? 'study' : 'studies'}`,
            severity: 'success',
          });
        } else {
          // This case shouldn't happen due to service logic, but just in case
          setSnackbar({
            open: true,
            message: 'No studies were deleted',
            severity: 'info',
          });
        }
        
        // Clear selections
        setSelectedStudyIds(new Set());
        setActiveSelectionPanel(null);
        
        // Close modal
        setDeleteModal({ open: false, type: null });
      } else {
        console.error('Delete failed:', result.error);
        
        // Show error message
        setSnackbar({
          open: true,
          message: result.error || 'Failed to delete studies. Please try again.',
          severity: 'error',
        });
        
        // Close modal
        setDeleteModal({ open: false, type: null });
      }
    } catch (error) {
      console.error('Delete error:', error);
      
      // Show error message
      setSnackbar({
        open: true,
        message: 'An unexpected error occurred. Please try again.',
        severity: 'error',
      });
      
      // Close modal
      setDeleteModal({ open: false, type: null });
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle Delete cancel/close
   */
  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, type: null });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  /**
   * Share patient or study with another user via email
   * @param {string} email - Target user's email address
   * @returns {Promise<Object>} Result object with success status and message
   */
  const handleShare = async (email) => {
    const { type, item } = shareModal;
    
    // Handle multi-select Share
    if (type === 'patient' || type === 'study') {
      if (hasSelection()) {
        const payload = getSelectionPayload();
        console.log('Sharing:', payload.studies);
        console.log('Derived patient names:', payload.patients);
        
        // Clear selections
        setSelectedStudyIds(new Set());
        setActiveSelectionPanel(null);
        
        return { success: true, message: 'Share payload logged to console' };
      }
    }
    
    // Legacy single-item share (from card action buttons - currently commented out)
    if (item) {
      if (type === 'patient') {
        return await libraryService.sharePatient(item.id, email);
      } else if (type === 'study') {
        return await libraryService.shareStudy(item.id, email);
      }
    }
    
    return { success: false, message: 'Invalid share type' };
  };

  /**
   * Get all study IDs for a given patient
   */
  const getPatientStudyIds = (patient) => {
    if (!patient || !patient.studies) return new Set();
    return new Set(patient.studies.map(s => s.id));
  };

  /**
   * Check if a patient is fully selected (all studies selected)
   */
  const isPatientFullySelected = (patient) => {
    const patientStudyIds = getPatientStudyIds(patient);
    if (patientStudyIds.size === 0) return false;
    
    return Array.from(patientStudyIds).every(id => selectedStudyIds.has(id));
  };

  /**
   * Check if a patient is partially selected (some but not all studies selected)
   */
  const isPatientPartiallySelected = (patient) => {
    const patientStudyIds = getPatientStudyIds(patient);
    if (patientStudyIds.size === 0) return false;
    
    const selectedCount = Array.from(patientStudyIds).filter(id => selectedStudyIds.has(id)).length;
    return selectedCount > 0 && selectedCount < patientStudyIds.size;
  };

  /**
   * Get total count of selected studies
   */
  const getSelectedStudyCount = () => selectedStudyIds.size;

  /**
   * Get count of selected patients (patients with all studies selected)
   */
  const getSelectedPatientCount = () => {
    return patientFiltersHook.filteredPatients.filter(p => isPatientFullySelected(p)).length;
  };

  /**
   * Check if any selection exists
   */
  const hasSelection = () => selectedStudyIds.size > 0;

  /**
   * Toggle study selection
   */
  const handleStudyCheckboxToggle = (e, studyId) => {
    e.stopPropagation(); // Prevent card click
    
    const newSelection = new Set(selectedStudyIds);
    if (newSelection.has(studyId)) {
      newSelection.delete(studyId);
    } else {
      newSelection.add(studyId);
    }
    setSelectedStudyIds(newSelection);
    setActiveSelectionPanel(newSelection.size > 0 ? 'studies' : null);
  };

  /**
   * Toggle patient selection (selects/unselects all studies for that patient)
   */
  const handlePatientCheckboxToggle = (e, patient) => {
    e.stopPropagation(); // Prevent card click
    
    const patientStudyIds = getPatientStudyIds(patient);
    const newSelection = new Set(selectedStudyIds);
    
    // If fully selected, unselect all
    if (isPatientFullySelected(patient)) {
      patientStudyIds.forEach(id => newSelection.delete(id));
    } else {
      // Otherwise, select all (handles both partial and none selected)
      patientStudyIds.forEach(id => newSelection.add(id));
    }
    
    setSelectedStudyIds(newSelection);
    setActiveSelectionPanel(newSelection.size > 0 ? 'patients' : null);
  };

  /**
   * Handle select-all for patients panel
   * Selects all studies from all filtered patients
   */
  const handlePatientSelectAll = () => {
    const allStudyIds = new Set();
    
    // Collect all study IDs from all filtered patients
    patientFiltersHook.filteredPatients.forEach(patient => {
      const patientStudyIds = getPatientStudyIds(patient);
      patientStudyIds.forEach(id => allStudyIds.add(id));
    });
    
    // If all selected, clear all. Otherwise, select all.
    if (isAllPatientsSelected()) {
      setSelectedStudyIds(new Set());
      setActiveSelectionPanel(null);
    } else {
      setSelectedStudyIds(allStudyIds);
      setActiveSelectionPanel('patients');
    }
  };

  /**
   * Handle select-all for studies panel
   * Selects all studies from the currently selected patient
   */
  const handleStudySelectAll = () => {
    if (!selectedPatient) return;
    
    const filteredStudyIds = new Set(
      studyFiltersHook.filteredStudies.map(s => s.id)
    );
    
    // If all filtered studies are selected, clear them. Otherwise, select all.
    if (isAllStudiesSelected()) {
      const newSelection = new Set(selectedStudyIds);
      filteredStudyIds.forEach(id => newSelection.delete(id));
      setSelectedStudyIds(newSelection);
      setActiveSelectionPanel(newSelection.size > 0 ? 'studies' : null);
    } else {
      const newSelection = new Set(selectedStudyIds);
      filteredStudyIds.forEach(id => newSelection.add(id));
      setSelectedStudyIds(newSelection);
      setActiveSelectionPanel('studies');
    }
  };

  /**
   * Check if all filtered patients are fully selected
   */
  const isAllPatientsSelected = () => {
    if (patientFiltersHook.filteredPatients.length === 0) return false;
    return patientFiltersHook.filteredPatients.every(p => isPatientFullySelected(p));
  };

  /**
   * Check if some (but not all) filtered patients are selected
   */
  const isSomePatientsSelected = () => {
    const selectedCount = patientFiltersHook.filteredPatients.filter(p => 
      isPatientFullySelected(p) || isPatientPartiallySelected(p)
    ).length;
    
    return selectedCount > 0 && !isAllPatientsSelected();
  };

  /**
   * Check if all filtered studies are selected
   */
  const isAllStudiesSelected = () => {
    if (!selectedPatient || studyFiltersHook.filteredStudies.length === 0) return false;
    return studyFiltersHook.filteredStudies.every(s => selectedStudyIds.has(s.id));
  };

  /**
   * Check if some (but not all) filtered studies are selected
   */
  const isSomeStudiesSelected = () => {
    if (!selectedPatient || studyFiltersHook.filteredStudies.length === 0) return false;
    
    const selectedCount = studyFiltersHook.filteredStudies.filter(s => 
      selectedStudyIds.has(s.id)
    ).length;
    
    return selectedCount > 0 && !isAllStudiesSelected();
  };

  /**
   * Get the currently active selection panel
   */
  const getActivePanel = () => activeSelectionPanel;

  /**
   * Get payload for Share/Delete operations
   * Returns selected study objects and derived patient names
   */
  const getSelectionPayload = () => {
    const selectedStudies = [];
    const patientNamesSet = new Set();
    
    // Collect all selected studies and their patient names
    patients.forEach(patient => {
      patient.studies.forEach(study => {
        if (selectedStudyIds.has(study.id)) {
          selectedStudies.push(study);
          patientNamesSet.add(patient.name);
        }
      });
    });
    
    return {
      studies: selectedStudies,
      patients: Array.from(patientNamesSet),
    };
  };

  /**
   * Transform patient object into display-ready key-value pairs for info modal
   * @param {Object} patient - Patient object
   * @returns {Object} Key-value pairs for display
   */
  const getPatientInfoData = (patient) => {
    if (!patient) return {};
    return {
      'Name': patient.name,
      'Date of Birth': patient.dob,
      'Patient ID': patient.phiSummary.patientId,
      'Sex': patient.phiSummary.sex,
      'Age': patient.phiSummary.age ? `${patient.phiSummary.age}Y` : 'N/A',
      'MRN': patient.metadata.mrn || 'N/A',
    };
  };

  /**
   * Transform study object into display-ready key-value pairs for info modal
   * @param {Object} study - Study object
   * @returns {Object} Key-value pairs for display
   */
  const getStudyInfoData = (study) => {
    if (!study) return {};
    return {
      'Description': study.description,
      'Study Date': study.date,
      'Study Time': study.time || 'N/A',
      'Modality': study.modality,
      'Study ID': study.metadata.studyID || 'N/A',
      'Accession Number': study.metadata.accessionNumber || 'N/A',
      'Institution Name': study.metadata.institutionName || 'N/A',
    };
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Tooltip title="Upload DICOM Files">
            <IconButton
              onClick={() => navigate('/upload')}
            >
              <Icon path={mdiCloudUploadOutline} size={1} />
            </IconButton>
          </Tooltip>
        </Box>
        <Tooltip title="Log Out">
          <IconButton
            onClick={handleLogout}
          >
            <Icon path={mdiLogout} size={1} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main Content - Two Panes */}
      <Box className={classes.content}>
        {/* Left Pane - Patient List */}
        <Box className={classes.pane}>
          <Typography variant="h6" className={classes.paneTitle}>
            Patients
          </Typography>
          
          <Box className={classes.searchContainer}>
            {/* Patient search - disabled when loading or no patients exist */}
            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Search patients (name, patient ID, or MRN)"
              value={patientSearchQuery}
              onChange={(e) => setPatientSearchQuery(e.target.value)}
              disabled={isLoading || patients.length === 0}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon path={mdiMagnify} size={1} />
                  </InputAdornment>
                ),
                endAdornment: patientSearchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setPatientSearchQuery('')}
                      edge="end"
                    >
                      <Icon path={mdiClose} size={1} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          {/* Patient sort and filter controls */}
          <Box className={classes.sortFilterContainer}>
            {/* Left side - Sort controls */}
            <Box className={classes.sortLeft}>
              <Typography className={classes.sortLabel}>Sort by:</Typography>
              <Select
                className={classes.patientSortSelect}
                value={patientFiltersHook.patientSort.key}
                onChange={(e) => {
                  patientFiltersHook.setPatientSort({ 
                    key: e.target.value, 
                    direction: patientFiltersHook.patientSort.direction 
                  });
                }}
                disabled={isLoading || patients.length === 0}
                size="small"
                variant="outlined"
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="dob">Date of Birth</MenuItem>
              </Select>
              <Tooltip title={
                (() => {
                  const { key, direction } = patientFiltersHook.patientSort;
                  if (key === 'name') {
                    return direction === 'asc' ? 'A - Z' : 'Z - A';
                  } else if (key === 'dob') {
                    return direction === 'asc' ? 'Oldest - Youngest' : 'Youngest - Oldest';
                  }
                  return '';
                })()
              }>
                <span>
                  <IconButton
                    onClick={() => {
                      patientFiltersHook.setPatientSort({
                        key: patientFiltersHook.patientSort.key,
                        direction: patientFiltersHook.patientSort.direction === 'asc' ? 'desc' : 'asc'
                      });
                    }}
                    disabled={isLoading || patients.length === 0}
                    size="small"
                  >
                    <Icon 
                      path={patientFiltersHook.patientSort.direction === 'asc' ? mdiChevronUp : mdiChevronDown} 
                      size={1} 
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            
            {/* Right side - DOB Filter */}
            <Box className={classes.filterRight}>
              <Typography className={classes.filterLabel}>Birth year:</Typography>
              <Select
                className={classes.yearSelect}
                variant="outlined"
                size="small"
                displayEmpty
                value={patientFiltersHook.tempDobFrom}
                onChange={patientFiltersHook.handleDobFromChange}
                disabled={isLoading || patients.length === 0}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span className={classes.yearSelectPlaceholder}>YYYY</span>;
                  }
                  return selected;
                }}
              >
                <MenuItem value="">
                  <em>YYYY</em>
                </MenuItem>
                {generateYearMenuItems()}
              </Select>
              <Typography className={classes.filterToText}>to</Typography>
              <Select
                className={classes.yearSelect}
                variant="outlined"
                size="small"
                displayEmpty
                value={patientFiltersHook.tempDobTo}
                onChange={patientFiltersHook.handleDobToChange}
                disabled={isLoading || patients.length === 0}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span className={classes.yearSelectPlaceholder}>YYYY</span>;
                  }
                  return selected;
                }}
              >
                <MenuItem value="">
                  <em>YYYY</em>
                </MenuItem>
                {generateYearMenuItems()}
              </Select>
              <Button
                className={classes.filterButton}
                onClick={patientFiltersHook.handleApplyDobFilter}
                disabled={isLoading || patients.length === 0 || (!patientFiltersHook.tempDobFrom && !patientFiltersHook.tempDobTo)}
                size="small"
                variant="contained"
                color="primary"
              >
                Apply
              </Button>
              <Button
                className={classes.filterButton}
                onClick={patientFiltersHook.handleClearDobFilter}
                disabled={isLoading || patients.length === 0}
                size="small"
                variant="outlined"
              >
                Clear
              </Button>
            </Box>
          </Box>

          {/* Patient selection header */}
          <Box className={classes.selectionHeader}>
            <Box className={classes.selectionControls}>
              <Checkbox 
                checked={isAllPatientsSelected()}
                indeterminate={isSomePatientsSelected()}
                onChange={handlePatientSelectAll}
                disabled={isLoading || patients.length === 0 || getActivePanel() === 'studies'}
                color="primary"
              />
              <Typography 
                className={classes.selectionCount}
                style={{ visibility: getActivePanel() === 'patients' && getSelectedPatientCount() > 0 ? 'visible' : 'hidden' }}
              >
                {getSelectedPatientCount() === 1 ? '1 patient selected' : `${getSelectedPatientCount()} patients selected`}
              </Typography>
              <Box className={classes.selectionActions}>
                <Tooltip title="Share">
                  <span>
                    <IconButton 
                      disabled={getActivePanel() !== 'patients' || getSelectedPatientCount() === 0}
                      onClick={handlePatientsShareClick}
                    >
                      <Icon path={mdiAccountPlusOutline} size={1} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete">
                  <span>
                    <IconButton 
                      disabled={getActivePanel() !== 'patients' || getSelectedPatientCount() === 0}
                      onClick={handlePatientsDeleteClick}
                    >
                      <Icon path={mdiTrashCanOutline} size={1} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          
          {isLoading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : patients.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6" className={classes.emptyStateText}>
                No DICOM studies available yet. Click 'Upload' to add your first study.
              </Typography>
            </Box>
          ) : patientFiltersHook.filteredPatients.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="body1" className={classes.emptyStateText}>
                No patients match your search and/or filters.
              </Typography>
            </Box>
          ) : (
            <Box className={classes.scrollableList}>
              {patientFiltersHook.filteredPatients.map((patient) => (
                <Card
                  key={patient.id}
                  className={`${classes.cardContainer} ${
                    selectedPatient?.id === patient.id ? classes.selectedCard : ''
                  }`}
                  onClick={() => handlePatientClick(patient)}
                >
                  <CardContent>
                    <Box className={classes.cardCheckboxContainer}>
                      <Box onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={isPatientFullySelected(patient)}
                          indeterminate={isPatientPartiallySelected(patient)}
                          onChange={(e) => handlePatientCheckboxToggle(e, patient)}
                          style={{ marginTop: -10 }}
                          color="primary"
                          disabled={getActivePanel() === 'studies'}
                        />
                      </Box>
                      <Box className={classes.cardContent}>
                        <Box className={classes.cardHeader}>
                          <Box>
                            <Typography className={classes.patientName}>
                              {patient.name}
                            </Typography>
                            <Typography className={classes.phiInfo}>
                              Date of Birth: {patient.dob}
                            </Typography>
                            <Typography className={classes.phiInfo}>
                              Patient ID: {patient.phiSummary.patientId}
                            </Typography>
                            <Typography className={classes.phiInfo}>
                              {patient.phiSummary.sex} • {patient.phiSummary.age ? `${patient.phiSummary.age}Y` : 'N/A'}
                            </Typography>
                          </Box>
                          <Box className={classes.cardActions}>
                            <Tooltip title="Patient Information">  
                              <IconButton 
                                size="small"
                                onClick={(e) => handlePatientInfo(e, patient)}
                              >
                                <Icon path={mdiInformationOutline} size={1} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        {/* Right Pane - Study List */}
        <Box className={classes.pane}>
          <Typography variant="h6" className={classes.paneTitle}>
            Studies
          </Typography>
          
          <Box className={classes.searchContainer}>
            {/* Study search - disabled when no patient selected or selected patient has no studies */}
            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Search studies (description, modality, study ID, or accession number)"
              value={studySearchQuery}
              onChange={(e) => setStudySearchQuery(e.target.value)}
              disabled={!selectedPatient || selectedPatient.studies.length === 0}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon path={mdiMagnify} size={1} />
                  </InputAdornment>
                ),
                endAdornment: studySearchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setStudySearchQuery('')}
                      edge="end"
                    >
                      <Icon path={mdiClose} size={1} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          {/* Study sort controls */}
          <Box className={classes.sortFilterContainer}>
            <Box className={classes.sortLeft}>
              <Typography className={classes.sortLabel}>Sort by:</Typography>
              <Select
                className={classes.studySortSelect}
                value={studyFiltersHook.studySort.key}
                onChange={(e) => {
                  studyFiltersHook.setStudySort({ 
                    key: e.target.value, 
                    direction: studyFiltersHook.studySort.direction 
                  });
                }}
                disabled={!selectedPatient || selectedPatient.studies.length === 0}
                size="small"
                variant="outlined"
              >
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="description">Description</MenuItem>
              </Select>
              <Tooltip title={
                (() => {
                  const { key, direction } = studyFiltersHook.studySort;
                  if (key === 'date') {
                    return direction === 'asc' ? 'Oldest - Newest' : 'Newest - Oldest';
                  } else if (key === 'description') {
                    return direction === 'asc' ? 'A - Z' : 'Z - A';
                  }
                  return '';
                })()
              }>
                <span>
                  <IconButton
                    onClick={() => {
                      studyFiltersHook.setStudySort({
                        key: studyFiltersHook.studySort.key,
                        direction: studyFiltersHook.studySort.direction === 'asc' ? 'desc' : 'asc'
                      });
                    }}
                    disabled={!selectedPatient || selectedPatient.studies.length === 0}
                    size="small"
                  >
                    <Icon 
                      path={studyFiltersHook.studySort.direction === 'asc' ? mdiChevronUp : mdiChevronDown} 
                      size={1} 
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            
            {/* Modality filter - middle */}
            <Box className={classes.filterMiddle}>
              <Typography className={classes.filterLabel}>Modality:</Typography>
              
              {/* Modality filter dropdown */}
              <Button
                className={`${classes.modalityButton} ${studyFiltersHook.modalityPopoverOpen ? 'open' : ''}`}
                onClick={studyFiltersHook.handleModalityClick}
                disabled={!selectedPatient || selectedPatient.studies.length === 0}
                size="small"
                variant="outlined"
                endIcon={<Icon path={studyFiltersHook.modalityPopoverOpen ? mdiMenuUp : mdiMenuDown} size={1} />}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                  {studyFiltersHook.modalityFilterLabel}
                </span>
              </Button>
            </Box>
            
            {/* Study date filter - right */}
            <Box className={classes.filterRight}>
              <Typography className={classes.filterLabel}>Study date:</Typography>
              <KeyboardDatePicker
                className={classes.filterDatePicker}
                views={['year', 'month']}
                format="MM/yyyy"
                openTo="year"
                minDate={new Date(1900, 0, 1)}
                maxDate={new Date()}
                value={studyFiltersHook.tempDateFrom}
                onChange={(date) => studyFiltersHook.setTempDateFrom(date)}
                disabled={!selectedPatient || selectedPatient.studies.length === 0}
                variant="inline"
                inputVariant="outlined"
                size="small"
                autoOk
                placeholder="MM/YYYY"
                keyboardIcon={<Icon path={mdiCalendar} size={0.9} />}
                InputProps={{
                  readOnly: true,
                  onClick: (e) => {
                    const iconButton = e.currentTarget.parentElement.querySelector('.MuiIconButton-root');
                    if (iconButton) iconButton.click();
                  },
                }}
              />
              <Typography className={classes.filterToText}>to</Typography>
              <KeyboardDatePicker
                className={classes.filterDatePicker}
                views={['year', 'month']}
                format="MM/yyyy"
                openTo="year"
                minDate={new Date(1900, 0, 1)}
                maxDate={new Date()}
                value={studyFiltersHook.tempDateTo}
                onChange={(date) => studyFiltersHook.setTempDateTo(date)}
                disabled={!selectedPatient || selectedPatient.studies.length === 0}
                variant="inline"
                inputVariant="outlined"
                size="small"
                autoOk
                placeholder="MM/YYYY"
                keyboardIcon={<Icon path={mdiCalendar} size={0.9} />}
                InputProps={{
                  readOnly: true,
                  onClick: (e) => {
                    const iconButton = e.currentTarget.parentElement.querySelector('.MuiIconButton-root');
                    if (iconButton) iconButton.click();
                  },
                }}
              />
              <Button
                className={classes.filterButton}
                onClick={studyFiltersHook.handleApplyStudyDateFilter}
                disabled={
                  !selectedPatient || 
                  selectedPatient.studies.length === 0 ||
                  (!studyFiltersHook.tempDateFrom && !studyFiltersHook.tempDateTo)
                }
                size="small"
                variant="contained"
                color="primary"
              >
                Apply
              </Button>
              <Button
                className={classes.filterButton}
                onClick={studyFiltersHook.handleClearStudyDateFilter}
                disabled={!selectedPatient || selectedPatient.studies.length === 0}
                size="small"
                variant="outlined"
              >
                Clear
              </Button>
            </Box>
          </Box>
          
          {/* Study selection header */}
          <Box className={classes.selectionHeader}>
            <Box className={classes.selectionControls}>
              <Checkbox 
                checked={isAllStudiesSelected()}
                indeterminate={isSomeStudiesSelected()}
                onChange={handleStudySelectAll}
                disabled={!selectedPatient || selectedPatient.studies.length === 0 || getActivePanel() === 'patients'}
                color="primary"
              />
              <Typography 
                className={classes.selectionCount}
                style={{ visibility: getActivePanel() === 'studies' && getSelectedStudyCount() > 0 ? 'visible' : 'hidden' }}
              >
                {getSelectedStudyCount() === 1 ? '1 study selected' : `${getSelectedStudyCount()} studies selected`}
              </Typography>
              <Box className={classes.selectionActions}>
                <Tooltip title="Share">
                  <span>
                    <IconButton 
                      disabled={getActivePanel() !== 'studies' || getSelectedStudyCount() === 0}
                      onClick={handleStudiesShareClick}
                    >
                      <Icon path={mdiAccountPlusOutline} size={1} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete">
                  <span>
                    <IconButton 
                      disabled={getActivePanel() !== 'studies' || getSelectedStudyCount() === 0}
                      onClick={handleStudiesDeleteClick}
                    >
                      <Icon path={mdiTrashCanOutline} size={1} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {!selectedPatient ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6" className={classes.emptyStateText}>
                Select a patient to view their studies
              </Typography>
            </Box>
          ) : selectedPatient.studies.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="body1" className={classes.emptyStateText}>
                No studies available.
              </Typography>
            </Box>
          ) : studyFiltersHook.filteredStudies.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="body1" className={classes.emptyStateText}>
                No studies match your search and/or filters.
              </Typography>
            </Box>
          ) : (
            <Box className={classes.scrollableList}>
              {studyFiltersHook.filteredStudies.map((study) => (
                <Card
                  key={study.id}
                  className={classes.cardContainer}
                  onClick={() => handleStudyClick(study)}
                >
                  <CardContent>
                    <Box className={classes.cardCheckboxContainer}>
                      <Box onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedStudyIds.has(study.id)}
                          onChange={(e) => handleStudyCheckboxToggle(e, study.id)}
                          style={{ marginTop: -10 }}
                          color="primary"
                          disabled={getActivePanel() === 'patients'}
                        />
                      </Box>
                      <Box className={classes.cardContent}>
                        <Box className={classes.cardHeader}>
                          <Box>
                            <Typography className={classes.studyDescription}>
                              {study.description}
                            </Typography>
                            <Typography className={classes.studyInfo}>
                              Study Date: {study.date}
                            </Typography>
                            <Typography className={classes.studyInfo}>
                              Modality: {study.modality}
                            </Typography>
                            <Typography className={classes.studyInfo}>
                              Accession Number: {study.metadata.accessionNumber}
                            </Typography>
                          </Box>
                          <Box className={classes.cardActions}>
                            <Tooltip title="Study Information"> 
                              <IconButton 
                                size="small"
                                onClick={(e) => handleStudyInfo(e, study)}
                              >
                                <Icon path={mdiInformationOutline} size={1} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Modality Filter Popover */}
      <Popover
        open={studyFiltersHook.modalityPopoverOpen}
        anchorEl={studyFiltersHook.modalityAnchorEl}
        onClose={studyFiltersHook.handleModalityClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className={classes.modalityPopover}
        disableAutoFocus
        disableEnforceFocus
      >
        <FormControl component="fieldset" fullWidth>
          <FormGroup>
            {studyFiltersHook.availableModalities.map(({ modality, count }) => (
              <FormControlLabel
                key={modality}
                className={classes.modalityCheckbox}
                control={
                  <Checkbox
                    checked={studyFiltersHook.selectedModalities.includes(modality)}
                    onChange={() => studyFiltersHook.handleModalityToggle(modality)}
                    size="small"
                    color="primary"
                  />
                }
                label={`${modality} (${count})`}
              />
            ))}
          </FormGroup>
          <Box className={classes.modalityActions}>
            <FormControlLabel
              className={classes.modalityCheckbox}
              control={
                <Checkbox
                  checked={
                    studyFiltersHook.availableModalities.length > 0 &&
                    studyFiltersHook.selectedModalities.length === studyFiltersHook.availableModalities.length
                  }
                  onChange={(e) => {
                    // Only allow checking (reset to default). Never allow "none selected".
                    if (e.target.checked) studyFiltersHook.handleSelectAllModalities();
                  }}
                  size="small"
                  color="primary"
                  disabled={studyFiltersHook.availableModalities.length === 0}
                />
              }
              label="Select All"
            />
          </Box>
        </FormControl>
      </Popover>

      {/* Patient Info Modal */}
      <InfoModal
        open={patientInfoModal.open}
        onClose={() => setPatientInfoModal({ open: false, patient: null })}
        title="Patient Information"
        data={getPatientInfoData(patientInfoModal.patient)}
      />

      {/* Study Info Modal */}
      <InfoModal
        open={studyInfoModal.open}
        onClose={() => setStudyInfoModal({ open: false, study: null })}
        title="Study Information"
        data={getStudyInfoData(studyInfoModal.study)}
      />

      {/* Share Modal */}
      <ShareModal
        open={shareModal.open}
        onClose={() => setShareModal({ open: false, type: null, item: null })}
        onShare={handleShare}
        title="Share With"
        itemType={shareModal.type === 'patient' ? 'Patient' : 'Study'}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={deleteModal.open}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemCount={deleteModal.type === 'patients' ? getSelectedPatientCount() : getSelectedStudyCount()}
        studyCount={getSelectedStudyCount()}
        type={deleteModal.type}
        isLoading={isDeleting}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </MuiPickersUtilsProvider>
  );
};

export default Library;